import { useState, useMemo } from 'react';
import { demoUsers, demoActivityLogs, demoCanteenBills } from '@/mocks/demoData';
import type { Shift } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';
import { getShifts, getActiveShift, addShift, endShift } from '@/utils/shiftStore';

const SHIFT_OPTIONS = [
  { name: 'Morning Shift', start: '06:00', end: '14:00' },
  { name: 'Evening Shift', start: '14:00', end: '22:00' },
  { name: 'Night Shift', start: '22:00', end: '06:00' },
];

function getCanteenBillsForShift(shift: Shift) {
  const shiftDate = new Date(shift.created_at).toISOString().slice(0, 10);
  const [startH, startM] = shift.start_time.split(':').map(Number);
  const [endH, endM] = shift.end_time.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  let endMinutes = endH * 60 + endM;
  if (endMinutes <= startMinutes) endMinutes += 24 * 60;

  return demoCanteenBills.filter((b) => {
    const bDate = new Date(b.created_at).toISOString().slice(0, 10);
    if (bDate !== shiftDate) return false;
    if (b.payment_method === 'room_charge') return false;
    const bH = new Date(b.created_at).getHours();
    const bM = new Date(b.created_at).getMinutes();
    let bMinutes = bH * 60 + bM;
    if (bMinutes < startMinutes && endMinutes > 24 * 60) bMinutes += 24 * 60;
    return bMinutes >= startMinutes && bMinutes < endMinutes;
  });
}

export default function ReceptionShifts() {
  const { addToast } = useApp();
  const [shifts, setShifts] = useState<Shift[]>(getShifts());
  const [showForm, setShowForm] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState<string | null>(null);
  const [showCanteenDetail, setShowCanteenDetail] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    shiftType: 'Morning Shift',
    openingBalance: '',
    assignedTo: '',
    handoverNotes: '',
  });

  const currentShift = getActiveShift();

  const handleStartShift = () => {
    if (!formData.openingBalance || !formData.assignedTo) {
      addToast('error', 'Opening balance and assigned staff are required');
      return;
    }
    const shiftDef = SHIFT_OPTIONS.find((s) => s.name === formData.shiftType)!;
    const newShift: Shift = {
      id: `shift-${Date.now()}`,
      name: shiftDef.name,
      start_time: shiftDef.start,
      end_time: shiftDef.end,
      assigned_to: formData.assignedTo,
      opening_balance: Number(formData.openingBalance),
      closing_balance: 0,
      collections: 0,
      cash_collections: 0,
      card_collections: 0,
      upi_collections: 0,
      canteen_collections: 0,
      canteen_cash: 0,
      canteen_card: 0,
      canteen_upi: 0,
      handover_notes: formData.handoverNotes,
      status: 'active',
      created_at: new Date().toISOString(),
    };
    addShift(newShift);
    setShifts(getShifts());
    setShowForm(false);
    setFormData({ shiftType: 'Morning Shift', openingBalance: '', assignedTo: '', handoverNotes: '' });
    addToast('success', `${shiftDef.name} started successfully`);
  };

  const handleEndShift = (shiftId: string) => {
    endShift(shiftId);
    setShifts(getShifts());
    addToast('success', 'Shift ended successfully');
  };

  const shiftStats = useMemo(() => {
    const today = new Date('2026-05-15');
    const todayShifts = shifts.filter((s) => new Date(s.created_at).toISOString().slice(0, 10) === today.toISOString().slice(0, 10));
    const totalCollections = todayShifts.reduce((sum, s) => sum + s.collections, 0);
    const totalCash = todayShifts.reduce((sum, s) => sum + (s.cash_collections || 0), 0);
    const totalCard = todayShifts.reduce((sum, s) => sum + (s.card_collections || 0), 0);
    const totalUpi = todayShifts.reduce((sum, s) => sum + (s.upi_collections || 0), 0);
    const totalCanteen = todayShifts.reduce((sum, s) => sum + (s.canteen_collections || 0), 0);
    const totalCanteenCash = todayShifts.reduce((sum, s) => sum + (s.canteen_cash || 0), 0);
    const totalCanteenCard = todayShifts.reduce((sum, s) => sum + (s.canteen_card || 0), 0);
    const totalCanteenUpi = todayShifts.reduce((sum, s) => sum + (s.canteen_upi || 0), 0);
    return {
      totalShifts: todayShifts.length,
      totalCollections,
      totalCash,
      totalCard,
      totalUpi,
      totalCanteen,
      totalCanteenCash,
      totalCanteenCard,
      totalCanteenUpi,
      discrepancies: todayShifts.filter((s) => s.status === 'closed' && s.closing_balance !== s.opening_balance + s.collections).length,
    };
  }, [shifts]);

  const getUserName = (userId: string) => demoUsers.find((u) => u.id === userId)?.full_name || 'Unknown';

  const refreshShifts = () => setShifts(getShifts());

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Shift Management</h1>
          <p className="text-navy-500 mt-1">Manage reception shifts, handovers, and cash balances.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Start Shift
        </button>
      </div>

      {/* Stats Row 1 — Room Collections */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-500">Shifts Today</span>
            <span className="w-9 h-9 rounded-lg bg-navy-50 flex items-center justify-center">
              <i className="ri-time-line text-navy-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">{shiftStats.totalShifts}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-500">Total Collections</span>
            <span className="w-9 h-9 rounded-lg bg-gold-50 flex items-center justify-center">
              <i className="ri-money-rupee-circle-line text-gold-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-navy-800">Rs. {shiftStats.totalCollections.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-500">Cash</span>
            <span className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
              <i className="ri-cash-line text-green-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-green-700">Rs. {shiftStats.totalCash.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-500">Card</span>
            <span className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <i className="ri-bank-card-line text-amber-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-amber-700">Rs. {shiftStats.totalCard.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-500">UPI</span>
            <span className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
              <i className="ri-qr-scan-line text-purple-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-purple-700">Rs. {shiftStats.totalUpi.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* Stats Row 2 — Canteen Collections */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-500">Canteen Revenue</span>
            <span className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
              <i className="ri-restaurant-line text-teal-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-teal-700">Rs. {shiftStats.totalCanteen.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-500">Canteen Cash</span>
            <span className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
              <i className="ri-cash-line text-green-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-green-700">Rs. {shiftStats.totalCanteenCash.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-500">Canteen Card</span>
            <span className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <i className="ri-bank-card-line text-amber-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-amber-700">Rs. {shiftStats.totalCanteenCard.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-navy-500">Canteen UPI</span>
            <span className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
              <i className="ri-qr-scan-line text-purple-600" />
            </span>
          </div>
          <div className="text-2xl font-bold text-purple-700">Rs. {shiftStats.totalCanteenUpi.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* Current Shift Card */}
      {currentShift && (
        <div className="bg-navy-800 rounded-xl p-6 mb-6 text-white">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gold-500 flex items-center justify-center">
                <i className="ri-time-line text-navy-900 text-xl" />
              </div>
              <div>
                <div className="text-lg font-semibold">{currentShift.name} <span className="text-green-400 text-sm font-normal">Active</span></div>
                <div className="text-navy-300 text-sm">{currentShift.start_time} - {currentShift.end_time} | Assigned: {getUserName(currentShift.assigned_to)}</div>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="text-center">
                <div className="text-navy-300 text-xs mb-1">Opening</div>
                <div className="font-semibold">Rs. {currentShift.opening_balance.toLocaleString('en-IN')}</div>
              </div>
              <div className="text-center">
                <div className="text-navy-300 text-xs mb-1">Cash</div>
                <div className="font-semibold text-green-400">Rs. {(currentShift.cash_collections || 0).toLocaleString('en-IN')}</div>
              </div>
              <div className="text-center">
                <div className="text-navy-300 text-xs mb-1">Card</div>
                <div className="font-semibold text-amber-400">Rs. {(currentShift.card_collections || 0).toLocaleString('en-IN')}</div>
              </div>
              <div className="text-center">
                <div className="text-navy-300 text-xs mb-1">UPI</div>
                <div className="font-semibold text-purple-400">Rs. {(currentShift.upi_collections || 0).toLocaleString('en-IN')}</div>
              </div>
              <div className="w-px h-10 bg-navy-700" />
              <div className="text-center">
                <div className="text-navy-300 text-xs mb-1">Canteen</div>
                <div className="font-semibold text-teal-400">Rs. {(currentShift.canteen_collections || 0).toLocaleString('en-IN')}</div>
              </div>
              <div className="text-center">
                <div className="text-navy-300 text-xs mb-1">Total</div>
                <div className="font-semibold text-gold-400">
                  Rs. {currentShift.collections.toLocaleString('en-IN')}
                </div>
              </div>
              <button
                onClick={() => handleEndShift(currentShift.id)}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                End Shift
              </button>
            </div>
          </div>
          {currentShift.handover_notes && (
            <div className="mt-4 pt-4 border-t border-navy-700 text-sm text-navy-300">
              <i className="ri-file-text-line mr-2" />
              {currentShift.handover_notes}
            </div>
          )}
        </div>
      )}

      {/* Shifts Table */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-navy-100 flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-semibold text-navy-800">Shift History</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCanteenDetail(showCanteenDetail ? null : 'all')}
              className="px-3 py-1.5 bg-teal-50 hover:bg-teal-100 rounded-lg text-xs font-medium text-teal-700 cursor-pointer transition-colors whitespace-nowrap"
            >
              {showCanteenDetail ? 'Hide Canteen' : 'Show Canteen'}
            </button>
            <button
              onClick={() => setShowBreakdown(showBreakdown ? null : 'all')}
              className="px-3 py-1.5 bg-navy-50 hover:bg-navy-100 rounded-lg text-xs font-medium text-navy-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              {showBreakdown ? 'Hide Breakdown' : 'Show Breakdown'}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Shift</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Time</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Assigned To</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Opening</th>
                {showBreakdown && (
                  <>
                    <th className="text-right px-3 py-3 font-semibold text-green-700">R-Cash</th>
                    <th className="text-right px-3 py-3 font-semibold text-amber-700">R-Card</th>
                    <th className="text-right px-3 py-3 font-semibold text-purple-700">R-UPI</th>
                  </>
                )}
                {showCanteenDetail && (
                  <>
                    <th className="text-right px-3 py-3 font-semibold text-teal-700 whitespace-nowrap">C-Cash</th>
                    <th className="text-right px-3 py-3 font-semibold text-teal-700 whitespace-nowrap">C-Card</th>
                    <th className="text-right px-3 py-3 font-semibold text-teal-700 whitespace-nowrap">C-UPI</th>
                    <th className="text-right px-3 py-3 font-semibold text-teal-700 whitespace-nowrap">C-Total</th>
                  </>
                )}
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Collections</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Closing</th>
                <th className="text-center px-5 py-3 font-semibold text-navy-700">Status</th>
                <th className="text-center px-5 py-3 font-semibold text-navy-700">Discrepancy</th>
              </tr>
            </thead>
            <tbody>
              {shifts.slice().reverse().map((shift) => {
                const expected = shift.opening_balance + shift.collections;
                const hasDiscrepancy = shift.status === 'closed' && shift.closing_balance !== expected;
                const canteenBills = getCanteenBillsForShift(shift);
                return (
                  <tr key={shift.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-navy-800">{shift.name}</td>
                    <td className="px-5 py-3.5 text-navy-500">{shift.start_time} - {shift.end_time}</td>
                    <td className="px-5 py-3.5 text-navy-600">{getUserName(shift.assigned_to)}</td>
                    <td className="px-5 py-3.5 text-right font-medium">Rs. {shift.opening_balance.toLocaleString('en-IN')}</td>
                    {showBreakdown && (
                      <>
                        <td className="px-3 py-3.5 text-right text-green-600">Rs. {(shift.cash_collections || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-3.5 text-right text-amber-600">Rs. {(shift.card_collections || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-3.5 text-right text-purple-600">Rs. {(shift.upi_collections || 0).toLocaleString('en-IN')}</td>
                      </>
                    )}
                    {showCanteenDetail && (
                      <>
                        <td className="px-3 py-3.5 text-right text-teal-600">Rs. {(shift.canteen_cash || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-3.5 text-right text-teal-600">Rs. {(shift.canteen_card || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-3.5 text-right text-teal-600">Rs. {(shift.canteen_upi || 0).toLocaleString('en-IN')}</td>
                        <td className="px-3 py-3.5 text-right font-semibold text-teal-700">Rs. {(shift.canteen_collections || 0).toLocaleString('en-IN')}</td>
                      </>
                    )}
                    <td className="px-5 py-3.5 text-right text-green-600">Rs. {shift.collections.toLocaleString('en-IN')}</td>
                    <td className="px-5 py-3.5 text-right font-medium">Rs. {shift.closing_balance.toLocaleString('en-IN')}</td>
                    <td className="px-5 py-3.5 text-center">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        shift.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-navy-100 text-navy-600'
                      }`}>
                        {shift.status === 'active' ? (
                          <><span className="w-1.5 h-1.5 rounded-full bg-green-500" />Active</>
                        ) : (
                          'Closed'
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      {hasDiscrepancy ? (
                        <span className="inline-flex items-center gap-1 text-red-600 font-medium text-xs">
                          <i className="ri-error-warning-line" />
                          Rs. {Math.abs(shift.closing_balance - expected).toLocaleString('en-IN')}
                        </span>
                      ) : shift.status === 'closed' ? (
                        <span className="text-green-600 text-xs"><i className="ri-checkbox-circle-line mr-1" />Match</span>
                      ) : (
                        <span className="text-navy-400 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Shift Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Start New Shift</h3>
              <button onClick={() => setShowForm(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Shift</label>
                <select
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  value={formData.shiftType}
                  onChange={(e) => setFormData((p) => ({ ...p, shiftType: e.target.value }))}
                >
                  {SHIFT_OPTIONS.map((s) => (
                    <option key={s.name} value={s.name}>{s.name} ({s.start} - {s.end})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Opening Balance (Rs.)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="Enter opening cash balance"
                  value={formData.openingBalance}
                  onChange={(e) => setFormData((p) => ({ ...p, openingBalance: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Assigned Staff</label>
                <select
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  value={formData.assignedTo}
                  onChange={(e) => setFormData((p) => ({ ...p, assignedTo: e.target.value }))}
                >
                  <option value="">Select staff member</option>
                  {demoUsers.filter((u) => u.role === 'receptionist').map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Handover Notes</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none"
                  placeholder="Any notes from previous shift..."
                  value={formData.handoverNotes}
                  onChange={(e) => setFormData((p) => ({ ...p, handoverNotes: e.target.value }))}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button
                onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleStartShift}
                className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer"
              >
                Start Shift
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}